import * as React from 'react';
import { Flex } from 'glints-aries';
import Link from 'next/link';
import { FormattedMessage } from 'react-intl';

import { ROUTES } from 'src/common/routes';
import { Alert } from 'src/components/Alert';

import { CanUserApplyWithReason, UserCannotApplyReason } from '../interface';
import * as S from './ImportantInfoAlert.sc';

const CannotApplyAlertRenderTitleMap: Partial<
  Record<
    UserCannotApplyReason,
    (props: { values: Record<string, any> }) => React.ReactElement
  >
> = {
  [UserCannotApplyReason.HAS_APPLIED]: () => {
    return (
      <FormattedMessage
        id="job-detail-page.has-applied-to-job"
        defaultMessage="You have applied for this job."
        tagName={S.AlertTitle}
      />
    );
  },
  [UserCannotApplyReason.HAS_CONNECTION]: () => (
    <FormattedMessage
      id="job-detail-page.has-connected-to-job"
      defaultMessage="You have been connected with this job by the company."
      tagName={S.AlertTitle}
    />
  ),
  [UserCannotApplyReason.HAS_RECOMMENDATION]: () => (
    <FormattedMessage
      id="job-detail-page.has-recommended-to-job"
      defaultMessage="You have been recommended by Glints recruiters for this job."
      tagName={S.AlertTitle}
    />
  ),
  [UserCannotApplyReason.HAS_BEEN_CONTACTED_BY_RECRUITER]: () => (
    <FormattedMessage
      id="job-detail-page.has-identified-your-profile"
      defaultMessage="Our in-house consultant has identified your profile as a potential great fit for this role! They will be reaching out to you shortly."
      tagName={S.AlertTitle}
    />
  ),
  [UserCannotApplyReason.NO_FOREIGN_APPLICANTS]: ({
    values,
  }: {
    values: Record<string, any>;
  }) => (
    <FormattedMessage
      id="job-detail-page.no-foreign-applicants"
      defaultMessage="This job accepts applications from candidates residing in {country} only."
      values={values}
      tagName={S.AlertTitle}
    />
  ),
};

type Props = {
  authenticated: boolean;
  canUserApplyWithReason: CanUserApplyWithReason;
  countryName?: string;
};

export const ImportantInfoAlert: React.FC<React.PropsWithChildren<Props>> = ({
  authenticated,
  canUserApplyWithReason,
  countryName,
}) => {
  const renderCannotApplyMessage =
    CannotApplyAlertRenderTitleMap[canUserApplyWithReason.reason];

  const shouldRenderTitle =
    authenticated &&
    !canUserApplyWithReason.canApply &&
    renderCannotApplyMessage;

  const showDescription =
    canUserApplyWithReason.reason === UserCannotApplyReason.HAS_CONNECTION ||
    canUserApplyWithReason.reason === UserCannotApplyReason.HAS_APPLIED;

  if (!shouldRenderTitle) return null;
  return (
    // @ts-ignore - To export component types from glints-aries
    <Alert py={16} px={24} mt={24} flexDirection="column">
      {renderCannotApplyMessage({
        values: { country: countryName },
      })}
      <If condition={showDescription}>
        <Flex mt={4}>
          <FormattedMessage
            id="job-detail-page.check-my-jobs"
            values={{
              link: (text: React.ReactNode) => (
                <Link href={`/${ROUTES.applications}`} target="_blank">
                  {text}
                </Link>
              ),
            }}
            defaultMessage="Check <link>my jobs</link> to track your application status."
            tagName={S.AlertDescription}
          />
        </Flex>
      </If>
    </Alert>
  );
};
